import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import ModalLayout from '../layouts/ModalLayout'
import {
  modalPrimaryButtonStyle,
  modalSecondaryButtonStyle
} from '../styles/buttonStyles'

const WebMessagesModal = ({
  open,
  onClose,
  webMessages,
  setWebMessages,
  size = 'medium'
}) => {
  const { t } = useTranslation()

  const [pressedBtn, setPressedBtn] = useState(null)
  const [hoverBtn, setHoverBtn] = useState(null)
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false)

  useEffect(() => {
    if (!open) return

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (confirmDeleteAll) {
          setConfirmDeleteAll(false)
          return
        }
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose, confirmDeleteAll])

  if (!open) return null

  const deleteAllNotifications = async () => {
    if (!webMessages.length) return

    await Promise.all(
      webMessages.map(msg =>
        fetchWithAuth(`/notifications/${msg.id}/web-delete`, {
          method: 'PATCH'
        })
      )
    )

    setWebMessages([])
    setConfirmDeleteAll(false)
  }

  return (
    <>
      <ModalLayout open={open} onClose={onClose} customWidth="1100px">

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
            padding: '0 16px',
            boxSizing: 'border-box'
          }}
        >

          <div style={{ paddingBottom: 20 }}>
            <h3 style={{ fontSize: 24, margin: 0 }}>
              {t('HOME_NOTIFICATIONS')}
            </h3>
          </div>

          <div
            style={{
              flex: 1,
              minHeight: 0,
              background: '#fff',
              border: '1px solid #107c6b',
              borderRadius: 12,
              padding: 16,
              overflowY: 'auto',
              overflowX: 'hidden',
              overscrollBehavior: 'contain',
              marginBottom: 12
            }}
          >
            {webMessages.length === 0 ? (
              <div style={{ opacity: 0.7, fontSize: 18 }}>
                {t('HOME_NOTIFICATIONS_NO_MESSAGE')}
              </div>
            ) : (
              webMessages.map(item => {
                const isRead = item.statusWeb === 'read'

                return (
                  <div
                    key={item.id}
                    style={{
                      padding: '10px 0',
                      borderBottom: '1px solid #ddd',
                      background: isRead ? '#fff' : '#f3fafa'
                    }}
                  >
                    <div style={{
                      fontWeight: isRead ? 600 : 700,
                      marginBottom: 4
                    }}>
                      {item.title}
                    </div>

                    <div style={{
                      fontSize: 16,
                      opacity: 0.8,
                      marginBottom: 12
                    }}>
                      {item.body || item.text}
                    </div>

                    <div style={{ display: 'flex', gap: 8 }}>

                      {!isRead && (
                        <button
                          style={modalSecondaryButtonStyle(
                            pressedBtn,
                            `read-${item.id}`,
                            hoverBtn === `read-${item.id}`
                          )}
                          onMouseEnter={() => setHoverBtn(`read-${item.id}`)}
                          onMouseLeave={() => {
                            setHoverBtn(null)
                            setPressedBtn(null)
                          }}
                          onMouseDown={() => setPressedBtn(`read-${item.id}`)}
                          onMouseUp={() => setPressedBtn(null)}
                          onClick={async () => {
                            await fetchWithAuth(`/notifications/${item.id}/web-read`, {
                              method: 'PATCH'
                            })

			setWebMessages(prev =>
 			 prev.map(x =>
 			   x.id === item.id
   			   ? { ...x, statusWeb: 'read' }
   			   : x
 			 )
			)
                          }}
                        >
                          {t('HOME_NOTIFICATIONS_READ')}
                        </button>
                      )}

                      <button
                        style={{
                          ...modalSecondaryButtonStyle(
                            pressedBtn,
                            `delete-${item.id}`,
                            hoverBtn === `delete-${item.id}`
                          ),
                          borderColor: '#c62828',
                          color: '#c62828'
                        }}
                        onMouseEnter={() => setHoverBtn(`delete-${item.id}`)}
                        onMouseLeave={() => {
                          setHoverBtn(null)
                          setPressedBtn(null)
                        }}
                        onMouseDown={() => setPressedBtn(`delete-${item.id}`)}
                        onMouseUp={() => setPressedBtn(null)}
                        onClick={async () => {
			await fetchWithAuth(`/notifications/${item.id}/web-delete`, {
			  method: 'PATCH'
			})
                          setWebMessages(prev =>
                            prev.filter(x => x.id !== item.id)
                          )
                        }}
                      >
                        {t('DELETE')}
                      </button>

                    </div>
                  </div>
                )
              })
            )}
          </div>

          <div
            style={{
              borderTop: '1px solid #ddd',
              paddingTop: 12,
              paddingBottom: 0,
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'center',
              gap: 10
            }}
          >

            <button
              style={{
                ...modalPrimaryButtonStyle(
                  pressedBtn,
                  'deleteAll',
                  hoverBtn === 'deleteAll'
                ),
                background: hoverBtn === 'deleteAll' ? '#8e0000' : '#c62828',
                borderColor: hoverBtn === 'deleteAll' ? '#8e0000' : '#c62828'
              }}
              onMouseEnter={() => setHoverBtn('deleteAll')}
              onMouseLeave={() => {
                setHoverBtn(null)
                setPressedBtn(null)
              }}
              onMouseDown={() => setPressedBtn('deleteAll')}
              onMouseUp={() => setPressedBtn(null)}
              onClick={() => {
                if (!webMessages.length) return
                setConfirmDeleteAll(true)
              }}
            >
              {t('DELETE_ALL')}
            </button>

            <button
              style={modalSecondaryButtonStyle(
                pressedBtn,
                'close',
                hoverBtn === 'close'
              )}
              onMouseEnter={() => setHoverBtn('close')}
              onMouseLeave={() => {
                setHoverBtn(null)
                setPressedBtn(null)
              }}
              onMouseDown={() => setPressedBtn('close')}
              onMouseUp={() => setPressedBtn(null)}
              onClick={onClose}
            >
              {t('CLOSE')}
            </button>

          </div>

        </div>
      </ModalLayout>

      {confirmDeleteAll && (
        <div
          onClick={() => setConfirmDeleteAll(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ededed',
              padding: '20px 30px',
              borderRadius: 14,
              border: '2px solid #107c6b',
              boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
              minWidth: 320
            }}
          >
            <h3 style={{ marginTop: 0 }}>
              {t('DELETE_ALL')}
            </h3>

            <p>{t('CONFIRM_DELETE_ALL')}</p>

            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 12,
                marginTop: 20
              }}
            >

              <button
                style={modalSecondaryButtonStyle(
                  pressedBtn,
                  'cancelDeleteAll',
                  hoverBtn === 'cancelDeleteAll'
                )}
                onMouseEnter={() => setHoverBtn('cancelDeleteAll')}
                onMouseLeave={() => {
                  setHoverBtn(null)
                  setPressedBtn(null)
                }}
                onMouseDown={() => setPressedBtn('cancelDeleteAll')}
                onMouseUp={() => setPressedBtn(null)}
                onClick={() => setConfirmDeleteAll(false)}
              >
                {t('CANCEL')}
              </button>

              <button
                style={{
                  ...modalPrimaryButtonStyle(
                    pressedBtn,
                    'confirmDeleteAll',
                    hoverBtn === 'confirmDeleteAll'
                  ),
                  background: '#c62828',
                  borderColor: '#c62828'
                }}
                onMouseEnter={() => setHoverBtn('confirmDeleteAll')}
                onMouseLeave={() => {
                  setHoverBtn(null)
                  setPressedBtn(null)
                }}
                onMouseDown={() => setPressedBtn('confirmDeleteAll')}
                onMouseUp={() => setPressedBtn(null)}
                onClick={deleteAllNotifications}
              >
                {t('TRIPLOG.BUTTON.CONFIRM')}
              </button>

            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default WebMessagesModal